import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Wrench, Sparkles, ThermometerSun } from 'lucide-react';
import { StatusItemCard } from './StatusItemCard';
import {
  type StatusItem,
  type StatusCategory,
  CATEGORY_KEYS,
  CATEGORY_ORDER,
} from './types';

interface StatusGridProps {
  statusItems: StatusItem[];
  onItemClick: (item: StatusItem) => void;
}

const CATEGORY_ICONS: Record<StatusCategory, React.ReactNode> = {
  CONSUMABLE: <Package className="w-4 h-4" />,
  EQUIPMENT: <Wrench className="w-4 h-4" />,
  CLEANLINESS: <Sparkles className="w-4 h-4" />,
  COMFORT: <ThermometerSun className="w-4 h-4" />,
};

export function StatusGrid({ statusItems, onItemClick }: StatusGridProps) {
  const { t } = useTranslation('scout');

  const grouped = useMemo(() => {
    const groups: Record<StatusCategory, StatusItem[]> = {
      CONSUMABLE: [],
      EQUIPMENT: [],
      CLEANLINESS: [],
      COMFORT: [],
    };

    statusItems.forEach((item) => {
      const category = item.statusType.category as StatusCategory;
      if (groups[category]) {
        groups[category].push(item);
      }
    });

    return groups;
  }, [statusItems]);

  if (statusItems.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      {CATEGORY_ORDER.map((category, categoryIndex) => {
        const items = grouped[category];
        if (items.length === 0) return null;

        return (
          <div
            key={category}
            className="animate-[fadeInUp_0.4s_ease-out_both]"
            style={{ animationDelay: `${categoryIndex * 80}ms` }}
          >
            {/* Category header */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-primary">
                {CATEGORY_ICONS[category]}
              </span>
              <span className="text-caption font-bold tracking-wide uppercase text-primary">
                {t(`status.categories.${CATEGORY_KEYS[category]}`)}
              </span>
              <div className="flex-1 h-px bg-gradient-to-r from-primary/20 to-transparent" />
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-3 gap-3">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="animate-[fadeInUp_0.3s_ease-out_both]"
                  style={{ animationDelay: `${(categoryIndex * 80) + (index * 50)}ms` }}
                >
                  <StatusItemCard
                    item={item}
                    onClick={() => onItemClick(item)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
